
import { throwError as observableThrowError, Observable, Observer } from 'rxjs';
import { Injectable, Injector } from '@angular/core';
import { Http, Headers, Response } from '@angular/http';
import { Account } from './account.service';
import { Config } from './config';
import { HttpService } from './http.service';
import { OAuthToken } from './oauth-token';
import { OAuthTokenRequest } from './oauth-token-request';
import { Token } from './token';
import { Registration } from './registration';
import { SftpRegistration } from './sftp_registration';

import { environment } from "../environments/environment";
import { OneRosterServiceMoc } from "./oneroster.service.moc";
import { rfc3339ToDate, isDateSet } from './date_helper';
import { RegistrationReply } from './registration_reply';
import { RegistrationRequest } from './registration_request';
import { Organization } from './organization';
import { AccountAssociation } from './account_association';
import { AccountAssociationRequest } from './account_association_request';
import { AccountAssociationReply } from './account_association_reply';
import { UserRoles } from './user_roles';
import { RegistrationFind } from './registration_find';
import { SchoolAccount } from './school_account';
import { FindAccountAssociation } from './account_association_find';
import { AccountAssociationCountReply } from './account_association_count_reply';
import { FindOrganization } from './find_organization';
import { RegistrationSyncRequest } from './registration_sync_request';
import { RegistrationSyncReply } from './registration_sync_reply';

@Injectable()
export class OneRosterService {
	userRolesKey: string = 'userRoles';
	timeout: number = 10000; //10 sec
	retry: number = 3; //max retry
	delay: number = 3000; //max delay after retry
	errors: any = {};

	constructor(private http: HttpService, private account: Account, private config: Config) {
		this.errors['default'] = 'There is an error when processing this request, please try again later.';
		this.errors['no connection'] = 'Your device seems to be offline, please try again later.'
		this.errors['unauthorized'] = 'Unauthorized access. Please try login again.';
		this.errors['not found'] = 'No results.';
		this.errors['invalid token'] = 'Unauthorized access. Please try login again.';
		this.errors['duplicate entry'] = 'This data already exists.';
		this.errors['bad request'] = 'Invalid request.';
		this.errors['internal server error'] = 'Internal server error. Please try again later.';
		this.errors['unexpected'] = 'Sorry, an unexpected error has occured. Please contact Tech Support.';
		this.errors['timeout'] = 'Timeout when communicating with a server, please try again later.';
		this.errors['email address is invalid'] = 'Email address is invalid.';
		this.errors['user status is invalid'] = 'Invalid user status.';
		this.errors['user status signup is invalid email not confirmed'] = 'The user you\'ve invited has not yet confirmed their email address.';
		this.errors['notification not supported'] = 'Push notifications are not supported in this browser.';
		this.errors['account license expired'] = 'Account License has expired. Please visit the District Manager Subscription page for more details and license renewal.';
		this.errors['invalid account license'] = 'Account License is invalid. Please visit the District Manager Subscription page for more details and license renewal.';
		this.errors['unauthorized access role'] = 'Unauthorized access role.';
		this.errors['oneroster license expired'] = 'OneRoster License has expired. Please visit the District Manager Subscription page for more details and license renewal.';
		this.errors['invalid oneroster license'] = 'OneRoster License is invalid. Please visit the District Manager Subscription page for more details and license renewal.';
	}

	validateAcctNumber(acctNumber: string): Observable<boolean> {
		let lusr = this.userRoles();
		if (lusr != null && lusr.acctNumber == acctNumber) {
			//return non http observable when the userroles exists at localStorage
			return Observable.create((observer: Observer<boolean>) => {
				observer.next(true);
				observer.complete();
			});
		}
		return Observable.create((observer: Observer<boolean>) => {
			observer.next(false);
			observer.complete();
		});
	}

	loadUser(): Observable<UserRoles> {
		return this.account.getUserProfileApi()
			.do(us => {
				this.account.setUser(us);
			})
			.mergeMap(() => {
				return this.userRolesApi()
					.map(urs => {
						this.setUserRoles(urs[0]);
						return urs[0]
					});
			});
	}

	loadUserByAcctNumber(acctNumber: string): Observable<UserRoles> {
		return this.account.getUserProfileApi()
			.do(us => {
				this.account.setUser(us);
			})
			.mergeMap(() => {
				return this.userRolesApi()
					.map(urs => {
						for (let u of urs) {
							if (u.acctNumber === acctNumber) {
								this.setUserRoles(u);
								return u;
							}
						}
						if (!acctNumber) {
							let usr = this.userRoles();
							if (usr){
								return usr
							}else {
								if(urs.length>0) {
									// Set default account
									this.setUserRoles(urs[0]);
									return urs[0]
								}
							}
						}
						throw new Error('unauthorized');
					});
			});
	}

	removeLocalStorage() {
		localStorage.removeItem(this.userRolesKey);
	}

	setUserRoles(us: UserRoles) {
		localStorage.setItem(this.userRolesKey, JSON.stringify(us));
	}

	userRoles() {
		if (localStorage.getItem(this.userRolesKey) === null) {
			return null
		}
		return JSON.parse(localStorage.getItem(this.userRolesKey));
	}

	getToken() {
		let t = this.account.getToken();
		if (!t) {
			t = new Token();
		}
		return t;
	}

	getError(err: any): Error {
		var er: any;
		var msg = 'no connection';
		if (typeof (err.json) === 'function') {
			er = err.json();
			if (er.message != null && er.message != null && er.message != '') {
				msg = er.message;
			} else if (er.error != null && er.error != null && er.error != '') {
				msg = er.error;
			} else {
				console.log(err);
			}
			return new Error(msg);
		} else if (err instanceof Error) {
			return err;
		} else {
			console.log(err);
		}
	}

	getUserError(err: Error): string {
		return this.errors[err.message];
	}

	isErrNotFound(err: Error): boolean {
		return err.message == 'not found';
	}

	isErrUnauthorized(err: Error): boolean {
		return err.message == 'unauthorized' ||
			err.message == 'unauthorized access role';
	}

	getUserErrorDefault(err: Error): string {
		let e = this.errors[err.message];
		if (e != null) {
			return e;
		}
		return this.errors['default'];
	}

	buildUrl(parameters) {
		var qs = "";
		for (var key in parameters) {
			var value = parameters[key];
			if (value != null && value != '') {
				qs += encodeURIComponent(key) + "=" + encodeURIComponent(value) + "&";
			}
		}
		if (qs.length > 0) {
			return qs.substring(0, qs.length - 1); //chop off last "&"
		}
		return '';
	}

	params(parameters) {
		var params: any = {}
		for (var key in parameters) {
			var value = parameters[key];
			if (value != null && value != '') {
				params[encodeURIComponent(key)] = encodeURIComponent(value);
			}
		}
		return params;
	}

	leftPad(val: string, size: number, ch: string): string {
		var result = String(val);
		if (!ch) {
			ch = " ";
		}
		while (result.length < size) {
			result = ch + result;
		}
		return result;
	}

	hexAcctNumber(num: number): string {
		//sign >>> is Zero-fill right shift
		return this.leftPad((num >>> 0).toString(16), 8, '0').toUpperCase();
	}

	// APIs
	userRolesApi(): Observable<UserRoles[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.get(this.config.districtOneRosterApiUrl + '/district-one-roster/userapi/roles', { headers: headers })
			.timeout(this.timeout)
			.retryWhen(error => this.httpRetry(error))
			.map(resp => {
				if (resp.status == 200) {
					var data = resp.json().list;
					var urs: UserRoles[] = [];
					for (var v in data) {
						urs.push({
							acctNumber: this.hexAcctNumber(data[v].acctNumber),
							schoolName: data[v].schoolName,
							isDefault: data[v].isDefault,
							roles: data[v].roles,
						});
					}
					return urs
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	districtAccountAssociationFindApi(acctNumber: string): Observable<SchoolAccount[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/districtaccountassociationapi/find`, { headers: headers })
			.timeout(this.timeout)
			.retryWhen(error => this.httpRetry(error))
			.map(resp => {
				if (resp.status == 200) {
					var data = resp.json().list;
					var sacc: SchoolAccount[] = [];
					for (var v of data) {
						sacc.push({
							id: v.id,
							acctNumber: v.acctNumber,
							organizationName: v.organizationName,
							parentAcctNumber: this.hexAcctNumber(v.parentAcctNumber),
							enabled: v.enabled,
						});
					}
					return sacc
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	setDefaultApi(acctNumber: string): Observable<boolean> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.put(this.config.districtOneRosterApiUrl + '/district/' + acctNumber + '/userapi/changedefaultrole', null, { headers: headers })
			.timeout(this.timeout)
			.retryWhen(error => this.httpRetry(error))
			.map(resp => {
				if (resp.status == 200) {
					return true;
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	// Registration
	registrationFindApi(acctNumber: string, req: RegistrationFind): Observable<Registration[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/registrationapi/find`, req, { headers: headers })
			.timeout(this.timeout)
			.retryWhen(error => this.httpRetry(error))
			.map(resp => {
				let rows: Registration[] = [];
				if (resp.status == 200) {
					var data = resp.json().list
					for (let v of data) {
						v.lastSyncErrorAt =new Date(v.lastSyncErrorAt);
						v.lastSyncedAt = new Date(v.lastSyncedAt);
						v.updatedAt = new Date(v.updatedAt);
						v.createdAt = new Date(v.createdAt);

						if (!isDateSet(v.lastSyncErrorAt)) {
							v.lastSyncErrorAt = null;
						}
						if (!isDateSet(v.lastSyncedAt)) {
							v.lastSyncedAt = null;
						}
						if (!isDateSet(v.updatedAt)) {
							v.updatedAt = null;
						}
						rows.push(v);
					}
					return rows;
				} else if (resp.status == 404) {
					console.log('404');
					return rows;
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	registrationAddApi(acctNumber: string, req: RegistrationRequest): Observable<RegistrationReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		let rq = JSON.stringify(req)
		return this.http.post(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/registrationapi`, rq, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	registrationUpdateApi(acctNumber: string, id: number, req: RegistrationRequest): Observable<RegistrationReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		let rq = JSON.stringify(req)
		return this.http.put(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/registrationapi/${id}`, rq, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	registrationRemoveApi(acctNumber: string, id: number): Observable<RegistrationReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.delete(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/registrationapi/${id}`, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	registrationSyncApi(acctNumber: string, id: number, req: RegistrationSyncRequest): Observable<RegistrationSyncReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		let rq = JSON.stringify(req)
		return this.http.put(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/registrationapi/sync/${id}`, rq, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	// SFTP Registration
	sftpRegistrationFindApi(acctNumber: string, req: any): Observable<SftpRegistration[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/sftpapi/find`, req, { headers: headers })
			.timeout(this.timeout)
			.retryWhen(error => this.httpRetry(error))
			.map(resp => {
				let rows: SftpRegistration[] = [];
				if (resp.status == 200) {
					var data = resp.json().list
					for (let v of data) {
						v.updatedAt = new Date(v.updatedAt);
						v.createdAt = new Date(v.createdAt);
						if (!isDateSet(v.updatedAt)) {
							v.updatedAt = null;
						}
						rows.push(v);
					}
					return rows;
				} else if (resp.status == 404) {
					return rows;
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	sftpRegistrationAddApi(acctNumber: string, req: any): Observable<any> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		let rq = JSON.stringify(req)
		return this.http.post(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/sftpapi/add`, rq, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	sftpRegistrationUpdateApi(acctNumber: string, req: any): Observable<any> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		let rq = JSON.stringify(req)
		return this.http.post(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/sftpapi/update`, rq, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	sftpRegistrationRemoveApi(acctNumber: string, req: any): Observable<any> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		let rq = JSON.stringify(req)
		return this.http.post(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/sftpapi/remove`, rq, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	sftpTestConnectionApi(acctNumber: string, req: SftpRegistration): Observable<string[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		let rq = JSON.stringify(req)
		return this.http.post(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/sftpapi/test-connection`, rq, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json().dirs;
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	sftpGenerateCredentialsApi(acctNumber: string): Observable<{ host: string; port: number; username: string; password: string }> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.get(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/sftpapi/generate-credentials`, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json();
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	sftpRegistrationSyncApi(acctNumber: string, id: number, req: RegistrationSyncRequest): Observable<RegistrationSyncReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		let rq = JSON.stringify(req)
		return this.http.put(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/sftpapi/sync/${id}`, rq, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	// Organization
	organizationFindApi(acctNumber: string, find: FindOrganization): Observable<Organization[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/organizationapi/find`, find, { headers: headers })
			.timeout(this.timeout)
			.retryWhen(error => this.httpRetryNo404(error))
			.map(resp => {
				let rows: Organization[] = [];
				if (resp.status == 200) {
					var data = resp.json().list
					for (let v of data) {
						v.updatedAt = new Date(v.updatedAt);
						if (!isDateSet(v.updatedAt)) {
							v.updatedAt = null;
						}
						v.createdAt = new Date(v.createdAt);
						rows.push(v);
					}
					return rows;
				} else if (resp.status == 404) {
					console.log('404');
					return rows;
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	// Account Association
	accountAssociationFindApi(acctNumber: string, findReq: FindAccountAssociation): Observable<AccountAssociation[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/accountassociationapi/find`, findReq, { headers: headers })
			.timeout(this.timeout)
			.retryWhen(error => this.httpRetryNo404(error))
			.map(resp => {
				let rows: AccountAssociation[] = [];
				if (resp.status == 200) {
					var data = resp.json().list
					for (let v of data) {
						v.districtAcctNumber = this.hexAcctNumber(v.districtAcctNumber);
						v.updatedAt = new Date(v.updatedAt);
						if (!isDateSet(v.updatedAt)) {
							v.updatedAt = null;
						}
						v.createdAt = new Date(v.createdAt);
						rows.push(v);
					}
					return rows;
				} else if (resp.status == 404) {
					console.log('404');
					return rows;
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	accountAssociationAddApi(acctNumber: string, req: AccountAssociationRequest): Observable<AccountAssociationReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		let rq = JSON.stringify(req)
		return this.http.post(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/accountassociationapi`, rq, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	accountAssociationUpdateApi(acctNumber: string, id: number, req: AccountAssociationRequest): Observable<AccountAssociationReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		let rq = JSON.stringify(req)
		return this.http.put(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/accountassociationapi/${id}`, rq, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	accountAssociationRemoveApi(acctNumber: string, id: number): Observable<AccountAssociationReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.delete(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/accountassociationapi/${id}`, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	accountAssociationFindCountApi(acctNumber: string, findReq: FindAccountAssociation): Observable<AccountAssociationCountReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.districtOneRosterApiUrl}/district-one-roster/${acctNumber}/accountassociationapi/count`, findReq, { headers: headers })
			.timeout(this.timeout)
			.retryWhen(error => this.httpRetryNo404(error))
			.map(resp => {
				let cr = new AccountAssociationCountReply();
				if (resp.status == 200) {
					var data = resp.json()
					cr.count = data.count;
					return cr;
				} else if (resp.status == 404) {
					console.log('404');
					return cr;
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	isErrDuplicateEntry(err: Error): boolean {
		return err.message == 'duplicate entry';
	}

	isErrNoConnection(err: Error): boolean {
		return err.message == 'no connection';
	}

	isErrAccountLicenseExpired(err: Error): boolean {
		return err.message == 'account license expired';
	}

	isErrAccountLicenseInvalid(err: Error): boolean {
		return err.message == 'invalid account license';
	}

	getDefaultSaveMessage(): string {
		return "Successfully​ Saved";
	}

	oauthTokenApi(req: OAuthTokenRequest): Observable<OAuthToken> {
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		let rq = JSON.stringify(req);

		// true at the end means skipping the http interception
		return this.http.post(`${this.config.districtOneRosterApiUrl}/oauth2/token`, rq, { headers: headers }, true)
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	httpRetry(o: Observable<any>): Observable<any> {
		return o
			.mergeMap((error, count) => {
				if (count < this.retry) {
					return Observable.of(error);
				}
				return observableThrowError(this.getError(error));
			})
			.delay(this.delay);
	}

	httpRetryNo404(o: Observable<any>): Observable<any> {
		return o
			.mergeMap((error, count) => {
				if (count < this.retry && error.status !== 404) {
					return Observable.of(error);
				}
				return observableThrowError(this.getError(error));
			})
			.delay(this.delay);
	}
}

export function OneRosterServiceFactory(
	http: HttpService,
	account: Account,
	config: Config
) {
	return (environment.isMoc) ? new OneRosterServiceMoc(http, account, config) : new OneRosterService(http, account, config);
}
